import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { SignatureCanvas } from '@/components/signature/SignatureCanvas';
import { CheckCircle, XCircle, Clock, AlertCircle, Loader2, ExternalLink, ArrowLeft, PenLine, CreditCard } from 'lucide-react';
import { format } from 'date-fns';
import { DocumentSheet } from '@/components/documents/DocumentSheet';
import { buildPublicEstimateDocument } from '@/components/documents/adapters';
import { formatMoney, resolveCompanyLocale } from '@/utils/locale';
import { safeReturnTo } from '@/portal/returnTo';

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

interface Company {
  name: string;
  logo_url: string | null;
  phone: string | null;
  email: string | null;
  primary_color: string | null;
  terms_url: string | null;
  privacy_url: string | null;
  abn: string | null;
  // Optional: present once the 2026-09-08 RPC migration is applied
  legal_name?: string | null;
  acn?: string | null;
  website?: string | null;
  country?: string | null;
  street_address?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
}

interface EstimateTax {
  rate: number | null;
  label: string | null;
  inclusive: boolean | null;
}

/** Deposit asked on acceptance (get-estimate-by-token / accept-estimate-signature). */
interface EstimateDeposit {
  percent: number | null;
  amount: number;
  paid: number;
  payment_url: string | null;
  reason?: string;
}

interface EstimateData {
  alreadySigned: boolean;
  deposit?: EstimateDeposit | null;
  company_country?: string | null;
  alreadyDeclined?: boolean;
  signedAt?: string;
  signerName?: string;
  id?: string;
  estimateNumber: string;
  customerName: string;
  customerAddress: string | null;
  serviceDescription: string | null;
  lineItems: LineItem[];
  subtotal: number;
  gstAmount: number;
  estimateAmount: number;
  validUntil: string | null;
  terms: string | null;
  createdAt?: string | null;
  company: Company;
  tax?: EstimateTax;
}

type PageState = 'loading' | 'error' | 'already-signed' | 'already-declined' | 'expired' | 'sign';
/** What the customer is doing on the sign page: choosing, accepting (signing) or declining */
type Mode = 'choose' | 'accept' | 'decline';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

/**
 * After acceptance: the deposit owed and a button to Square's secure card page. When the link
 * could not be made at signing time the customer can ask for it again; if the company takes
 * deposits some other way, they are told the company will be in touch.
 */
function DepositPayPanel({ token, deposit, country, primaryColor }: {
  token: string | undefined;
  deposit: EstimateDeposit;
  country: string | null | undefined;
  primaryColor: string;
}) {
  const [url, setUrl] = useState<string | null>(deposit.payment_url);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(
    !deposit.payment_url && deposit.reason && deposit.reason !== 'paid' ? deposit.reason : null,
  );
  const locale = resolveCompanyLocale(country);
  const paid = deposit.paid >= deposit.amount - 0.004;

  const fetchLink = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/estimate-deposit-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const body = await res.json();
      if (body?.deposit?.payment_url) {
        setUrl(body.deposit.payment_url);
        setProblem(null);
      } else {
        setProblem(body?.deposit?.reason ?? 'unavailable');
      }
    } catch {
      setProblem('unavailable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border p-4 text-left">
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          Deposit{deposit.percent ? ` (${deposit.percent}%)` : ''}
        </span>
        <span className="ml-auto text-lg font-semibold tabular-nums">{formatMoney(deposit.amount, locale)}</span>
      </div>
      {paid ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-success">
          <CheckCircle className="h-4 w-4" /> Deposit received, thank you.
        </p>
      ) : url ? (
        <>
          <Button asChild size="lg" className="mt-3 w-full gap-2" style={{ backgroundColor: primaryColor }}>
            <a href={url}><CreditCard className="h-4 w-4" /> Pay deposit now</a>
          </Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Secure card payment processed by Square.</p>
        </>
      ) : problem === 'not_connected' || problem === 'no_location' || problem === 'currency_mismatch' ? (
        <p className="mt-2 text-sm text-muted-foreground">The company will be in touch about paying the deposit.</p>
      ) : (
        <Button variant="outline" className="mt-3 w-full gap-2" onClick={fetchLink} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {problem ? 'Try the payment link again' : 'Get payment link'}
        </Button>
      )}
    </div>
  );
}

/** Shown on the outcome cards when the customer arrived from their portal. */
function BackToAccount({ to }: { to: string | null }) {
  if (!to) return null;
  return (
    <Button asChild variant="outline" className="mt-4 gap-2">
      <Link to={to}><ArrowLeft className="h-4 w-4" /> Back to your account</Link>
    </Button>
  );
}

export default function EstimateSign() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  // Only ever a /portal/<slug>/... path (open-redirect guard)
  const returnTo = safeReturnTo(searchParams.get('return'));
  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  
  // Form state
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [estimateTermsAccepted, setEstimateTermsAccepted] = useState(false);
  const [legalPoliciesAccepted, setLegalPoliciesAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [acceptedDeposit, setAcceptedDeposit] = useState<EstimateDeposit | null>(null);

  // Accept / decline choice
  const [mode, setMode] = useState<Mode>('choose');
  const [declineReason, setDeclineReason] = useState('');
  const [declineSuccess, setDeclineSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setPageState('error');
      return;
    }

    fetchEstimate();
  }, [token]);

  const fetchEstimate = async () => {
    try {
      // invoke() doesn't support query params, so call the function URL directly
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/get-estimate-by-token?token=${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError('Estimate not found or has expired');
          setPageState('expired');
          return;
        }
        throw new Error('Failed to load estimate');
      }

      const estimateData = await response.json();

      if (estimateData.error) {
        setError(estimateData.error);
        setPageState('error');
        return;
      }

      setEstimate(estimateData);

      if (estimateData.alreadySigned) {
        setPageState('already-signed');
      } else if (estimateData.alreadyDeclined) {
        setPageState('already-declined');
      } else {
        // Pre-fill signer name from customer name
        if (estimateData.customerName) {
          setSignerName(estimateData.customerName);
        }
        setPageState('sign');
      }
    } catch (err) {
      console.error('Error fetching estimate:', err);
      setError('Failed to load estimate. Please try again.');
      setPageState('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signatureImage || !signerName || !estimateTermsAccepted || !legalPoliciesAccepted) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/accept-estimate-signature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            signatureImage,
            signerName: signerName.trim(),
            estimateTermsAccepted,
            legalPoliciesAccepted,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Failed to submit signature');
      }

      setAcceptedDeposit(result.deposit && Number(result.deposit.amount) > 0 ? result.deposit : null);
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error submitting signature:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit signature');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signerName.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/decline-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: signerName.trim(),
          reason: declineReason.trim() || null,
        }),
      });

      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || 'Failed to submit your response');
      }

      setDeclineSuccess(true);
    } catch (err) {
      console.error('Error declining estimate:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit your response');
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (next: Mode) => {
    setError(null);
    setMode(next);
  };

  const canSubmit = signatureImage && signerName.trim() && estimateTermsAccepted && legalPoliciesAccepted;

  // Build the printable document model once per payload (currency/labels follow the supplier's country)
  const doc = useMemo(() => {
    if (!estimate || estimate.alreadySigned) return null;
    return buildPublicEstimateDocument(estimate, resolveCompanyLocale(estimate.company?.country));
  }, [estimate]);
  const primaryColor = estimate?.company?.primary_color || 'hsl(var(--primary))';

  // Render loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading estimate...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Unable to Load Estimate</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render expired state
  if (pageState === 'expired') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <Clock className="h-16 w-16 text-warning mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Estimate Expired</h2>
            <p className="text-muted-foreground">
              This estimate link has expired, been withdrawn, or is no longer valid.
            </p>
            <p className="text-muted-foreground mt-2">
              Please contact the company for a new estimate.
            </p>
            <BackToAccount to={returnTo} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render already signed state
  if (pageState === 'already-signed' && estimate) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            {estimate.company.logo_url ? (
              <img 
                src={estimate.company.logo_url} 
                alt={estimate.company.name}
                className="h-12 mx-auto mb-2 object-contain"
              />
            ) : (
              <h1 className="text-xl font-bold" style={{ color: primaryColor }}>
                {estimate.company.name}
              </h1>
            )}
          </CardHeader>
          <CardContent className="pb-8">
            <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Estimate Already Signed</h2>
            <p className="text-muted-foreground mb-4">
              This estimate ({estimate.estimateNumber}) was signed by{' '}
              <strong>{estimate.signerName}</strong> on{' '}
              {estimate.signedAt && format(new Date(estimate.signedAt), 'd MMMM yyyy \'at\' h:mm a')}.
            </p>
            {estimate.deposit && (
              <DepositPayPanel token={token} deposit={estimate.deposit} country={estimate.company_country} primaryColor={primaryColor} />
            )}
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground">
              {estimate.deposit && estimate.deposit.paid < estimate.deposit.amount - 0.004
                ? 'If you have questions:'
                : 'No further action is required. If you have questions:'}
            </p>
            {estimate.company.phone && (
              <p className="text-sm mt-2">
                <strong>Phone:</strong> {estimate.company.phone}
              </p>
            )}
            {estimate.company.email && (
              <p className="text-sm">
                <strong>Email:</strong> {estimate.company.email}
              </p>
            )}
            <BackToAccount to={returnTo} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render already declined state (link reused after a decline)
  if (pageState === 'already-declined' && estimate) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            {estimate.company.logo_url ? (
              <img
                src={estimate.company.logo_url}
                alt={estimate.company.name}
                className="h-12 mx-auto mb-2 object-contain"
              />
            ) : (
              <h1 className="text-xl font-bold" style={{ color: primaryColor }}>
                {estimate.company.name}
              </h1>
            )}
          </CardHeader>
          <CardContent className="pb-8">
            <XCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Estimate Declined</h2>
            <p className="text-muted-foreground mb-4">
              Estimate <strong>{estimate.estimateNumber}</strong> has already been declined and can no longer be accepted online.
            </p>
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground">
              Changed your mind? Get in touch and we can issue a fresh estimate.
            </p>
            {estimate.company.phone && (
              <p className="text-sm mt-2">
                <strong>Phone:</strong> {estimate.company.phone}
              </p>
            )}
            {estimate.company.email && (
              <p className="text-sm">
                <strong>Email:</strong> {estimate.company.email}
              </p>
            )}
            <BackToAccount to={returnTo} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render declined (just now) state
  if (declineSuccess && estimate) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            {estimate.company.logo_url ? (
              <img
                src={estimate.company.logo_url}
                alt={estimate.company.name}
                className="h-12 mx-auto mb-2 object-contain"
              />
            ) : (
              <h1 className="text-xl font-bold" style={{ color: primaryColor }}>
                {estimate.company.name}
              </h1>
            )}
          </CardHeader>
          <CardContent className="pb-8">
            <XCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Estimate Declined</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for letting us know. Estimate <strong>{estimate.estimateNumber}</strong> has been marked as declined.
            </p>
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground">
              If anything changes, or you would like a revised quote, we are happy to help.
            </p>
            {estimate.company.phone && (
              <p className="text-sm mt-2">
                <strong>Phone:</strong> {estimate.company.phone}
              </p>
            )}
            {estimate.company.email && (
              <p className="text-sm">
                <strong>Email:</strong> {estimate.company.email}
              </p>
            )}
            <BackToAccount to={returnTo} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render success state
  if (submitSuccess && estimate) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            {estimate.company.logo_url ? (
              <img 
                src={estimate.company.logo_url} 
                alt={estimate.company.name}
                className="h-12 mx-auto mb-2 object-contain"
              />
            ) : (
              <h1 className="text-xl font-bold" style={{ color: primaryColor }}>
                {estimate.company.name}
              </h1>
            )}
          </CardHeader>
          <CardContent className="pb-8">
            <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Estimate Accepted!</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for accepting estimate <strong>{estimate.estimateNumber}</strong>.
            </p>
            <div className="bg-muted rounded-lg p-4 mb-4">
              <p className="text-sm">
                <strong>Total:</strong> {formatMoney(estimate.estimateAmount, resolveCompanyLocale(estimate.company?.country))}
                {estimate.tax?.label && estimate.tax?.rate
                  ? ` (${estimate.tax.inclusive ? 'inc.' : 'ex.'} ${estimate.tax.label})`
                  : ''}
              </p>
              <p className="text-sm mt-1">
                <strong>Signed by:</strong> {signerName}
              </p>
            </div>
            {acceptedDeposit && (
              <DepositPayPanel token={token} deposit={acceptedDeposit} country={estimate.company?.country} primaryColor={primaryColor} />
            )}
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground">
              {acceptedDeposit
                ? 'Once the deposit is paid, the company will be in touch to book the work.'
                : 'The company will be in touch shortly to arrange the next steps.'}
            </p>
            {estimate.company.phone && (
              <p className="text-sm mt-2">
                <strong>Phone:</strong> {estimate.company.phone}
              </p>
            )}
            {estimate.company.email && (
              <p className="text-sm">
                <strong>Email:</strong> {estimate.company.email}
              </p>
            )}
            <BackToAccount to={returnTo} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render sign form
  if (!estimate) return null;

  return (
    <div className="min-h-screen bg-muted py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {returnTo && (
          <Link
            to={returnTo}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to your account
          </Link>
        )}

        {/* The quotation itself — same A4 document the office prints */}
        {doc && <DocumentSheet doc={doc} variant="screen" className="mb-6" />}

        {/* Step 1: accept or decline */}
        {mode === 'choose' && (
          <Card>
            <CardHeader>
              <CardTitle>Your Response</CardTitle>
              <CardDescription>
                Please review the estimate above, then let us know how you would like to proceed.
                {estimate.deposit && estimate.deposit.amount > 0 && (
                  <> Accepting asks for a deposit of{' '}
                    <strong>{formatMoney(estimate.deposit.amount, resolveCompanyLocale(estimate.company?.country))}</strong>
                    {estimate.deposit.percent ? ` (${estimate.deposit.percent}%)` : ''}, paid by card.
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  size="lg"
                  className="h-auto flex-col items-start gap-1 py-4 text-left whitespace-normal"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => switchMode('accept')}
                >
                  <span className="flex items-center gap-2 text-base font-semibold">
                    <PenLine className="h-4 w-4" /> Accept &amp; sign
                  </span>
                  <span className="text-xs font-normal opacity-90">
                    Sign electronically and agree to the terms
                  </span>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="h-auto flex-col items-start gap-1 py-4 text-left whitespace-normal"
                  onClick={() => switchMode('decline')}
                >
                  <span className="flex items-center gap-2 text-base font-semibold">
                    <XCircle className="h-4 w-4" /> Decline
                  </span>
                  <span className="text-xs font-normal text-muted-foreground">
                    Let us know this estimate is not for you
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Decline path */}
        {mode === 'decline' && (
          <Card>
            <CardHeader>
              <CardTitle>Decline Estimate</CardTitle>
              <CardDescription>
                Tell us you would rather not go ahead with estimate {estimate.estimateNumber}. A reason is optional but helps us improve.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleDecline} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="declinerName">Your Name</Label>
                  <Input
                    id="declinerName"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="declineReason">Reason (optional)</Label>
                  <Textarea
                    id="declineReason"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value.slice(0, 1000))}
                    placeholder="e.g. Went with another provider, price too high, no longer needed"
                    rows={3}
                  />
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive rounded-lg p-3 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex flex-col-reverse gap-3 sm:flex-row">
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-2 sm:w-auto"
                    onClick={() => switchMode('choose')}
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    size="lg"
                    className="flex-1"
                    disabled={!signerName.trim() || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Decline this estimate'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Accept path: signature + terms */}
        {mode === 'accept' && (
        <Card>
          <CardHeader>
            <CardTitle>Accept Estimate</CardTitle>
            <CardDescription>
              Sign below and confirm the terms to accept estimate {estimate.estimateNumber}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Signature Canvas */}
              <SignatureCanvas
                onSignatureChange={setSignatureImage}
                width={500}
                height={150}
              />

              {/* Signer Name */}
              <div className="space-y-2">
                <Label htmlFor="signerName">Your Name</Label>
                <Input
                  id="signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Legal Consent Box */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <p className="text-sm font-medium">By signing below, I acknowledge that:</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>I have reviewed and accept this estimate</li>
                  <li>I authorize the work described above</li>
                  <li>My signature, name, and device information will be recorded as proof of acceptance</li>
                  <li>This constitutes a legally binding agreement</li>
                </ul>
              </div>

              {/* Checkboxes */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="estimateTerms"
                    checked={estimateTermsAccepted}
                    onCheckedChange={(checked) => setEstimateTermsAccepted(checked === true)}
                  />
                  <label
                    htmlFor="estimateTerms"
                    className="text-sm leading-tight cursor-pointer"
                  >
                    I accept the estimate terms and conditions above
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="legalPolicies"
                    checked={legalPoliciesAccepted}
                    onCheckedChange={(checked) => setLegalPoliciesAccepted(checked === true)}
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="legalPolicies"
                      className="text-sm leading-tight cursor-pointer block"
                    >
                      I agree to the company's Terms & Conditions and Privacy Policy
                    </label>
                    <div className="flex gap-3 text-xs">
                      {estimate.company.terms_url && (
                        <a
                          href={estimate.company.terms_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          View Terms & Conditions <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {estimate.company.privacy_url && (
                        <a
                          href={estimate.company.privacy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          View Privacy Policy <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-destructive/10 text-destructive rounded-lg p-3 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              {estimate.deposit && estimate.deposit.amount > 0 && (
                <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
                  <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>
                    A deposit of <strong>{formatMoney(estimate.deposit.amount, resolveCompanyLocale(estimate.company?.country))}</strong>
                    {estimate.deposit.percent ? ` (${estimate.deposit.percent}%)` : ''} is payable by card once you accept.
                  </span>
                </p>
              )}
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-2 sm:w-auto"
                  onClick={() => switchMode('choose')}
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  className="flex-1"
                  disabled={!canSubmit || isSubmitting}
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Sign & Accept Estimate'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>Questions? Contact {estimate.company.name}</p>
          {estimate.company.phone && <p>Phone: {estimate.company.phone}</p>}
          {estimate.company.email && <p>Email: {estimate.company.email}</p>}
          {estimate.company.abn && (
            <p>{resolveCompanyLocale(estimate.company.country).taxIdLabels.abn}: {estimate.company.abn}</p>
          )}
        </div>
      </div>
    </div>
  );
}
